import React, { useCallback, useMemo, useState } from 'react';
import type { ProductStoreRoute } from '@atlassiansox/product-store-react';
import { Drawer, DrawerContent } from '@atlaskit/drawer/compiled';
import type { IntegrationViewProps, ViewState } from './types';
import { combineExtensions, getInitialState } from './utils';
import { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { toCrossFlowOrigin } from '../utils/toCrossFlowOrigin';
import type { CrossFlowExtensions, TargetType } from '@atlassiansox/cross-flow-base-types';
import { flushSync } from 'react-dom';
import type { CompletionStatus } from '@atlassiansox/cross-flow-api-internals';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { ViewStateContext, type ViewStateContextType } from './context';
import { CrossFlowSupportHeader } from './header';
import { Box } from '@atlaskit/primitives';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
	},
	content: {
		marginTop: token('space.0'),
	},
});

export const CompiledDrawerContainer = ({ children }: React.PropsWithChildren<unknown>) => {
	return (
		<Box testId="cross-flow-support-content" xcss={styles.container}>
			{children}
		</Box>
	);
};

export const CompiledIntegrationView = ({
	onAnalyticsEvent,
	onHandShake,
	onClose,
	ProductStoreComponent,
	CrossFlowComponent,
	cloudId,
	journey,
	targetProduct,
	originProduct,
	sourceComponent,
	sourceContext,
	locale,
	edgePrefix,
	theme,
	redirectToWac,
	zIndex,
	env,
	experimentalOptions,
	extensions,
	plugins,
}: IntegrationViewProps) => {
	const [isDrawerOpen, setIsDrawerOpen] = useState(true);
	const [completionStatus, setCompletionStatus] = useState<CompletionStatus>({});
	const [viewState, setViewState] = useState<ViewState>(
		getInitialState({ journey, targetProduct, sourceComponent, sourceContext }),
	);

	const analyticsContext = useMemo(
		() => ({ cloudId, journey, targetProduct, originProduct, sourceComponent, sourceContext }),
		[cloudId, journey, targetProduct, originProduct, sourceComponent, sourceContext],
	);

	const closeDrawer = useCallback(() => {
		setIsDrawerOpen(false);
	}, []);

	/**
	 * Cross-flow Drawer Close
	 */
	const handleOnClose = useCallback(
		(_: any, analyticEvent: UIAnalyticsEvent) => {
			const { context, payload } = analyticEvent;
			onAnalyticsEvent({
				context: [context, analyticsContext],
				payload: {
					...payload,
					actionSubjectId: 'closeDrawerButton',
				},
			});
			closeDrawer();
		},
		[onAnalyticsEvent, closeDrawer, analyticsContext],
	);

	const handleOnCloseComplete = useCallback(() => {
		onClose(completionStatus);
	}, [onClose, completionStatus]);

	/**
	 * Product Store Handlers
	 */
	const handleProductStoreHandshake = useCallback(() => {
		onHandShake('productStoreSpa');
	}, [onHandShake]);

	const handleProductStoreOnChangeRoute = useCallback((route: ProductStoreRoute) => {
		setViewState((prevState: ViewState) => ({
			...prevState,
			route,
		}));
	}, []);

	const handleProductStoreOnTryClicked = useCallback(
		(product: TargetType, extensions?: CrossFlowExtensions) => {
			setViewState((prevState: ViewState) => ({
				...prevState,
				spa: 'cross-flow-frontend',
				targetProduct: product,
				extensions,
			}));
		},
		[],
	);

	/**
	 * Cross-flow Frontend Handlers
	 */
	const handleCrossFlowHandshake = useCallback(() => {
		onHandShake('crossFlowSpa');
	}, [onHandShake]);

	const handleCrossFlowOnClose = useCallback(() => {
		if (journey === 'get-started') {
			/**
			 * User has completed Get Started journey.
			 * Cross Flow Frontend does not provide status, but only invokes the callback
			 * when activation was complete
			 *
			 * NOTE: flushSync required to ensure completion status is updated before
			 * running the drawer status.
			 */
			flushSync(() => {
				setCompletionStatus({
					success: true,
				});
			});
		}
		closeDrawer();
	}, [closeDrawer, journey]);

	const contextState = useMemo<ViewStateContextType>(() => {
		return {
			state: viewState,
			setViewState: setViewState,
		};
	}, [viewState, setViewState]);

	const extensionsWithOptionalProductStoreExtensions = useMemo(() => {
		return viewState.spa === 'cross-flow-frontend' && viewState.extensions
			? combineExtensions(extensions ?? {}, viewState.extensions)
			: extensions;
	}, [viewState, extensions]);

	return (
		<Drawer
			width="full"
			zIndex={zIndex}
			isOpen={isDrawerOpen}
			onClose={handleOnClose}
			onCloseComplete={handleOnCloseComplete}
			testId="cross-flow-support-drawer"
		>
			<ViewStateContext.Provider value={contextState}>
				<CompiledDrawerContainer>
					<CrossFlowSupportHeader />
					<DrawerContent xcss={styles.content}>
						{viewState.spa === 'product-store' && (
							<ProductStoreComponent
								edgePrefix={edgePrefix}
								cloudId={cloudId}
								locale={locale}
								route={viewState.route}
								originProduct={originProduct}
								theme={theme}
								experimentalOptions={experimentalOptions}
								extensions={extensions}
								onClose={closeDrawer}
								onTryClicked={handleProductStoreOnTryClicked}
								onAnalyticsEvent={onAnalyticsEvent}
								onChangeRoute={handleProductStoreOnChangeRoute}
								plugins={plugins}
								onHandShake={handleProductStoreHandshake}
								isStandaloneProductPage={journey === 'decide'}
								isLinkExpansion={redirectToWac}
								env={env}
								sourceContext={sourceContext}
								sourceComponent={sourceComponent}
								journey={journey}
							/>
						)}
						{viewState.spa === 'cross-flow-frontend' && (
							<CrossFlowComponent
								edgePrefix={edgePrefix}
								onClose={handleCrossFlowOnClose}
								cloudId={cloudId}
								locale={locale}
								sourceContext={sourceContext}
								sourceComponent={sourceComponent}
								targetProduct={viewState.targetProduct}
								originProduct={toCrossFlowOrigin(originProduct)}
								theme={theme}
								experimentalOptions={experimentalOptions}
								extensions={extensionsWithOptionalProductStoreExtensions}
								onAnalyticsEvent={onAnalyticsEvent}
								onHandShake={handleCrossFlowHandshake}
								plugins={plugins}
								env={env}
								journey={journey}
							/>
						)}
					</DrawerContent>
				</CompiledDrawerContainer>
			</ViewStateContext.Provider>
		</Drawer>
	);
};
